import React, { useMemo } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { fillTimeline, fmtMs, fmtNum, fmtTick, fmtWib } from "./monUtil";

const EMPTY = { count: 0, errors: 0, c4xx: 0, rpm: 0, p50_ms: null, p95_ms: null, p99_ms: null, avg_ms: null, max_ms: null, error_rate: 0 };

function Tip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-xl border border-line bg-white p-3 text-xs shadow-lg">
      <p className="font-bold text-slate-900">{fmtWib(new Date(p.ts).toISOString(), { dateStyle: "short", timeStyle: "short" })}</p>
      <p className="mt-1 text-slate-600">p50 <b>{fmtMs(p.p50_ms)}</b> · p95 <b>{fmtMs(p.p95_ms)}</b> · p99 <b>{fmtMs(p.p99_ms)}</b></p>
      <p className="text-slate-600">{fmtNum(p.count)} req · {fmtNum(p.rpm)}/mnt · maks {fmtMs(p.max_ms)}</p>
      <p className={p.errors ? "font-bold text-rose-600" : "text-slate-500"}>{p.errors} error 5xx{p.c4xx ? ` · ${p.c4xx} 4xx` : ""}</p>
    </div>
  );
}

export function LatencyChart({ data, win }) {
  const pts = useMemo(() => (data ? fillTimeline(data.points || [], win, data.step_min, EMPTY) : []), [data, win]);
  if (!data) return <div className="h-80 animate-pulse rounded-2xl bg-slate-50" />;
  const tick = (v) => fmtTick(v, win);
  return (
    <div data-testid="mon-latency-chart">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">Persentil latensi</p>
      <div className="h-60">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={pts} syncId="lat" margin={{ left: 0, right: 8, top: 4 }}>
            <CartesianGrid stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="ts" type="number" domain={["dataMin", "dataMax"]} scale="time" tickFormatter={tick} tick={{ fontSize: 10, fill: "#94A3B8" }} minTickGap={40} />
            <YAxis tickFormatter={fmtMs} tick={{ fontSize: 10, fill: "#94A3B8" }} width={60} />
            <Tooltip content={<Tip />} /><Legend wrapperStyle={{ fontSize: 11 }} />
            <Line dataKey="p50_ms" name="p50" stroke="#06B6D4" strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls={false} />
            <Line dataKey="p95_ms" name="p95" stroke="#2563EB" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls={false} />
            <Line dataKey="p99_ms" name="p99" stroke="#7C3AED" strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive={false} connectNulls={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="mb-2 mt-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">Throughput & error</p>
      <div className="h-32">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={pts} syncId="lat" margin={{ left: 0, right: 8 }}>
            <CartesianGrid stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="ts" type="number" domain={["dataMin", "dataMax"]} scale="time" tickFormatter={tick} tick={{ fontSize: 10, fill: "#94A3B8" }} minTickGap={40} />
            <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} width={60} allowDecimals={false} />
            <Tooltip content={<Tip />} /><Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="rpm" name="req/menit" fill="#06B6D4" isAnimationActive={false} />
            <Bar dataKey="errors" name="error 5xx" fill="#F43F5E" isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
